import axios, { isAxiosError } from 'axios';

import {
  IMPORT_REQUEST_TIMEOUT_MS,
  isItineraryImportDraft,
  isItineraryImportErrorBody,
  type ItineraryImportDraft,
  type ItineraryImportErrorCode,
} from '@/models/itineraryImportTypes';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

export class ItineraryImportError extends Error {
  readonly code: ItineraryImportErrorCode;
  readonly retryable: boolean;
  readonly httpStatus: number;

  constructor(params: {
    code: ItineraryImportErrorCode;
    message: string;
    retryable: boolean;
    httpStatus: number;
  }) {
    super(params.message);
    this.name = 'ItineraryImportError';
    this.code = params.code;
    this.retryable = params.retryable;
    this.httpStatus = params.httpStatus;
  }
}

export type ItineraryImportSource =
  | { type: 'text'; text: string }
  | { type: 'pdf' | 'docx'; key: string }
  | { type: 'instagram' | 'tiktok'; url: string };

export interface RequestItineraryImportParams {
  source: ItineraryImportSource;
  import_id: string;
  import_started_at: string;
  force?: boolean;
  signal?: AbortSignal;
}

function buildAuthHeaders(): Record<string, string> | undefined {
  if (typeof window === 'undefined') return undefined;
  const rawAccessToken = localStorage.getItem('accessToken');
  if (!rawAccessToken) return undefined;
  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: accessToken,
  };
  const rawIdToken = localStorage.getItem('idToken');
  if (rawIdToken) {
    headers['x-id-token'] = rawIdToken.startsWith('Bearer ')
      ? rawIdToken.substring(7)
      : rawIdToken;
  }
  return headers;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseItineraryImportResponseBody(data: unknown): ItineraryImportDraft {
  if (isItineraryImportErrorBody(data)) {
    throw new ItineraryImportError({
      code: data.code,
      message: data.message,
      retryable: data.retryable ?? false,
      httpStatus: 200,
    });
  }

  const draftPayload =
    isRecord(data) && data.status === 'success' && 'data' in data ? data.data : data;

  if (!isItineraryImportDraft(draftPayload)) {
    throw new ItineraryImportError({
      code: 'UPSTREAM_FAILED',
      message: 'Unexpected import response.',
      retryable: true,
      httpStatus: 200,
    });
  }

  return draftPayload;
}

function mapAxiosToImportError(err: unknown): ItineraryImportError {
  if (axios.isCancel(err) || (isAxiosError(err) && err.code === 'ERR_CANCELED')) {
    const canceled = new ItineraryImportError({
      code: 'UPSTREAM_FAILED',
      message: 'Import canceled.',
      retryable: true,
      httpStatus: 503,
    });
    canceled.name = 'AbortError';
    return canceled;
  }

  if (isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const data = err.response?.data;
    if (isItineraryImportErrorBody(data)) {
      return new ItineraryImportError({
        code: data.code,
        message: data.message,
        retryable: data.retryable ?? status >= 500,
        httpStatus: status || 400,
      });
    }
    if (status === 401) {
      return new ItineraryImportError({
        code: 'UPSTREAM_FAILED',
        message: 'Please sign in to import.',
        retryable: false,
        httpStatus: 401,
      });
    }
  }

  return new ItineraryImportError({
    code: 'UPSTREAM_FAILED',
    message: 'Unable to import this content. Please try again.',
    retryable: true,
    httpStatus: 503,
  });
}

export async function requestItineraryImport(
  params: RequestItineraryImportParams,
): Promise<ItineraryImportDraft> {
  if (!API_URL) {
    throw new ItineraryImportError({
      code: 'UPSTREAM_FAILED',
      message: 'API base URL is not configured.',
      retryable: false,
      httpStatus: 0,
    });
  }

  const headers = buildAuthHeaders();
  const url = `${API_URL}/api/v2/itinerary/import`;

  try {
    const response = await axios.post<unknown>(
      url,
      {
        source: params.source,
        import_id: params.import_id,
        import_started_at: params.import_started_at,
        force: params.force ?? false,
      },
      {
        headers,
        timeout: IMPORT_REQUEST_TIMEOUT_MS,
        signal: params.signal,
      },
    );

    return parseItineraryImportResponseBody(response.data);
  } catch (err) {
    if (err instanceof ItineraryImportError) {
      throw err;
    }
    throw mapAxiosToImportError(err);
  }
}
